import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils.js';
import { useMe, useLogout } from '../hooks/useAuth.js';
import {
  LayoutDashboard,
  Upload,
  List,
  BookOpen,
  LogOut,
  ChevronRight,
  Layers,
  FolderOpen,
  Users,
  Building2,
  Tag,
  Pencil,
  Lock,
  Gift,
  Ticket,
} from 'lucide-react';

const nav = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/upload', label: 'Upload', icon: Upload },
  { href: '/batches', label: 'Batches', icon: List },
  { href: '/redemptions', label: 'Redemption Codes', icon: Gift },
  { href: '/coupons', label: 'Coupons', icon: Ticket },
];

const deployEnv = import.meta.env.VITE_DEPLOY_ENV ?? '';
const showStagingBanner = deployEnv.toLowerCase() === 'staging';

const catalogNav = [
  { href: '/catalog', label: 'Items', icon: BookOpen },
  { href: '/series', label: 'Series', icon: Layers },
  { href: '/groups', label: 'Groupings', icon: FolderOpen },
  { href: '/contributors', label: 'Cast & Crew', icon: Users },
  { href: '/publishers', label: 'Publishers', icon: Building2 },
  { href: '/genres', label: 'Genres', icon: Tag },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data: me } = useMe();
  const logout = useLogout();

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {showStagingBanner && (
        <div
          className="shrink-0 bg-amber-50 text-amber-900/70 text-center text-[11px] font-medium py-1 border-b border-amber-200/80"
          role="status"
        >
          Staging · non-production data
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-60 flex-shrink-0 bg-brand-950 flex flex-col">
          {/* Logo */}
          <div className="px-6 py-5 border-b border-white/10">
            <span className="text-white font-bold text-lg tracking-tight">🎙 Carefree</span>
            <p className="text-brand-300 text-xs mt-0.5">Soundbooth Production Admin</p>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {nav.map(({ href, label, icon: Icon }) => {
              const active = location.pathname === href || (href !== '/' && location.pathname.startsWith(href));
              return (
                <Link
                  key={href}
                  to={href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    active
                      ? 'bg-brand-600 text-white'
                      : 'text-brand-200 hover:bg-white/10 hover:text-white',
                  )}
                >
                  <Icon size={16} />
                  {label}
                  {active && <ChevronRight size={14} className="ml-auto" />}
                </Link>
              );
            })}

            <div className="border-t border-white/10 mt-2 mb-1" />
            {catalogNav.map(({ href, label, icon: Icon }) => {
              const active = location.pathname === href || (href !== '/' && location.pathname.startsWith(href));
              return (
                <Link
                  key={href}
                  to={href}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                    active
                      ? 'bg-brand-600 text-white'
                      : 'text-brand-200 hover:bg-white/10 hover:text-white',
                  )}
                >
                  <Icon size={16} />
                  {label}
                  {active && <ChevronRight size={14} className="ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Edit-mode indicator */}
          {me && (
            <div className={`mx-3 mb-2 px-3 py-2 rounded-lg flex items-center gap-2 text-xs font-medium ${
              me.enableEdit
                ? 'bg-green-900/50 text-green-300 border border-green-700/40'
                : 'bg-red-900/30 text-red-300 border border-red-700/30'
            }`}>
              {me.enableEdit ? <Pencil size={12} /> : <Lock size={12} />}
              {me.enableEdit ? 'Edit mode: ON' : 'Edit mode: OFF'}
            </div>
          )}

          {/* User / Logout */}
          <div className="px-3 py-4 border-t border-white/10">
            {me && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
                <div className="w-7 h-7 rounded-full bg-brand-600 flex items-center justify-center text-white text-xs font-bold uppercase">
                  {me.email?.[0] ?? 'U'}
                </div>
                <span className="text-brand-200 text-xs truncate flex-1">{me.email}</span>
                <button
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  className="text-brand-400 hover:text-white transition-colors disabled:opacity-50"
                  title="Logout"
                >
                  <LogOut size={14} />
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
